// =============================================================================
// Product/Inventory Add/Edit — faithful merge of POS frmNewProducts +
// IMS Inventory_Add. Identity, plant taxonomy, pricing, quantities, physical
// attributes, and status flags (active/depleted/price book/print picture).
// =============================================================================
import { useState, useEffect } from 'react'
import { X, Search, Image as ImageIcon } from 'lucide-react'
import { createProduct, updateProduct, searchPicturesForProduct, getPictureByLegacyId } from '../services/data'
import { Card, Button } from './shared/ui'
import type { Product, InventoryPicture } from '../types'
import { toast } from 'react-toastify'

type Mode = 'new' | 'edit'
const TYPES = ['Plants', 'Materials', 'Labor', 'Rental', 'Hardgoods']

interface Props { mode: Mode; product?: Product | null; onClose: () => void; onSaved: (p: Product) => void }

const empty = {
  sku: '', scan_number: '', name: '', alt_description: '',
  genus: '', species: '', variety: '', inventory_type: '',
  cost_price: '', retail_price: '', sale_price: '', wholesale_price: '', tax_percent: '',
  qty_on_hand: '', qty_remaining: '', qty_ordered: '', qty_received: '',
  size: '', unit: '', color: '', supplier_name: '', inventory_year: '', freight: '',
  is_active: true, is_depleted: false, in_price_book: false, print_picture: false,
  notes: '',
}

function Field({ label, value, onChange, type = 'text', className = '' }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; className?: string
}) {
  return (
    <div className={className}>
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
    </div>
  )
}

export default function ProductForm({ mode, product, onClose, onSaved }: Props) {
  const init = product
    ? { ...empty, ...Object.fromEntries(Object.keys(empty).map(k => {
        const v = (product as Record<string, unknown>)[k]
        return [k, typeof v === 'boolean' ? v : (v ?? empty[k as keyof typeof empty])]
      })) }
    : { ...empty }
  const [f, setF] = useState<typeof empty>(init as typeof empty)
  // Shared inventory-picture link (legacy products.picture_id -> inventory_pictures.legacy_id).
  // Many items can reference the same library photo. We cache the chosen image_url
  // for fast display, but picture_id is the real shared link.
  const [pictureId, setPictureId] = useState<number | null>(product?.picture_id ?? null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(product?.image_url ?? null)
  const [picName, setPicName] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const set = (k: keyof typeof empty, v: string | boolean) => setF(prev => ({ ...prev, [k]: v }))
  const num = (v: string) => v === '' ? null : Number(v)

  // Resolve the linked library picture on edit (for the thumbnail + name).
  useEffect(() => {
    if (product?.picture_id) {
      getPictureByLegacyId(product.picture_id, product.inventory_type ?? null)
        .then(p => { if (p) { setPreviewUrl(p.image_url ?? null); setPicName(p.name ?? null) } })
        .catch(() => {})
    }
  }, [product])

  const choosePic = (p: InventoryPicture) => {
    setPictureId(p.legacy_id ?? null)
    setPreviewUrl(p.image_url ?? null)
    setPicName(p.name ?? null)
    setPickerOpen(false)
  }
  const clearPic = () => { setPictureId(null); setPreviewUrl(null); setPicName(null) }

  const doSave = async () => {
    if (!f.name.trim()) { toast.error('Description (name) is required'); return }
    setSaving(true)
    try {
      const payload: Partial<Product> = {
        sku: f.sku || null, scan_number: f.scan_number || null, name: f.name,
        alt_description: f.alt_description || null,
        genus: f.genus || null, species: f.species || null, variety: f.variety || null,
        inventory_type: f.inventory_type || null,
        cost_price: num(f.cost_price), retail_price: num(f.retail_price), sale_price: num(f.sale_price),
        wholesale_price: num(f.wholesale_price), tax_percent: num(f.tax_percent),
        qty_on_hand: num(f.qty_on_hand), qty_remaining: num(f.qty_remaining) as number | null,
        qty_ordered: num(f.qty_ordered) as number | null, qty_received: num(f.qty_received) as number | null,
        size: f.size || null, unit: f.unit || null, color: f.color || null,
        supplier_name: f.supplier_name || null, inventory_year: num(f.inventory_year) as number | null,
        freight: num(f.freight),
        is_active: f.is_active, is_depleted: f.is_depleted, in_price_book: f.in_price_book, print_picture: f.print_picture,
        picture_id: pictureId,
        image_url: previewUrl,
        notes: f.notes || null,
      }
      const saved = mode === 'edit' && product
        ? await updateProduct(product.id, payload)
        : await createProduct(payload)
      toast.success('Product saved!')
      onSaved(saved)
    } catch (e) {
      console.error(e)
      toast.error('Failed to save product')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">{mode === 'edit' ? 'Edit Product' : 'New Product'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Identity</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="SKU / Scan / Barcode" value={f.sku} onChange={v => { set('sku', v); set('scan_number', v) }} />
              <div>
                <label className="text-xs font-medium text-gray-500">Type</label>
                <select value={f.inventory_type} onChange={e => set('inventory_type', e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm">
                  <option value="">—</option>
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <Field label="Description" value={f.name} onChange={v => set('name', v)} className="md:col-span-3" />
              <Field label="Genus" value={f.genus} onChange={v => set('genus', v)} />
              <Field label="Species" value={f.species} onChange={v => set('species', v)} />
              <Field label="Variety" value={f.variety} onChange={v => set('variety', v)} />
            </div>
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Pricing</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Field label="Cost" value={f.cost_price} onChange={v => set('cost_price', v)} type="number" />
              <Field label="Retail" value={f.retail_price} onChange={v => set('retail_price', v)} type="number" />
              <Field label="Sale" value={f.sale_price} onChange={v => set('sale_price', v)} type="number" />
              <Field label="Wholesale" value={f.wholesale_price} onChange={v => set('wholesale_price', v)} type="number" />
              <Field label="Tax %" value={f.tax_percent} onChange={v => set('tax_percent', v)} type="number" />
            </div>
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Quantities</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="On Hand" value={f.qty_on_hand} onChange={v => set('qty_on_hand', v)} type="number" />
              <Field label="Remaining" value={f.qty_remaining} onChange={v => set('qty_remaining', v)} type="number" />
              <Field label="Ordered" value={f.qty_ordered} onChange={v => set('qty_ordered', v)} type="number" />
              <Field label="Received" value={f.qty_received} onChange={v => set('qty_received', v)} type="number" />
            </div>
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Physical / Source</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Size" value={f.size} onChange={v => set('size', v)} />
              <Field label="Unit" value={f.unit} onChange={v => set('unit', v)} />
              <Field label="Color" value={f.color} onChange={v => set('color', v)} />
              <Field label="Supplier" value={f.supplier_name} onChange={v => set('supplier_name', v)} />
              <Field label="Inventory Year" value={f.inventory_year} onChange={v => set('inventory_year', v)} type="number" />
              <Field label="Freight" value={f.freight} onChange={v => set('freight', v)} type="number" />
            </div>
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Photo</h4>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                {previewUrl
                  ? <img src={previewUrl} alt={picName ?? ''} className="object-cover w-full h-full" />
                  : <ImageIcon size={28} className="text-gray-300" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700">
                  {picName ? picName : pictureId != null ? `Picture #${pictureId}` : 'No picture selected'}
                </p>
                <p className="text-xs text-gray-400 mb-2">
                  Pictures are shared from the Inventory Picture library — many items can use the same photo.
                </p>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setPickerOpen(true)}>Choose from Library</Button>
                  {pictureId != null && <Button variant="ghost" onClick={clearPic}>Remove</Button>}
                </div>
              </div>
            </div>
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Status</h4>
            <div className="flex flex-wrap gap-4 text-sm text-gray-700">
              <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_active} onChange={e => set('is_active', e.target.checked)} /> Active</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_depleted} onChange={e => set('is_depleted', e.target.checked)} /> Depleted</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={f.in_price_book} onChange={e => set('in_price_book', e.target.checked)} /> In Price Book</label>
              <label className="flex items-center gap-2"><input type="checkbox" checked={f.print_picture} onChange={e => set('print_picture', e.target.checked)} /> Print Picture</label>
            </div>
          </section>

          <section>
            <label className="text-xs font-medium text-gray-500">Notes</label>
            <textarea value={f.notes} onChange={e => set('notes', e.target.value)} rows={2}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Close</Button>
          <Button onClick={doSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </Card>
      {pickerOpen && <PicturePicker onPick={choosePic} onClose={() => setPickerOpen(false)} />}
    </div>
  )
}

// Searchable grid of inventory-picture library entries (those with a photo).
// Selecting one links the product to that shared picture by picture_id.
function PicturePicker({ onPick, onClose }: { onPick: (p: InventoryPicture) => void; onClose: () => void }) {
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<InventoryPicture[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let active = true
    setLoading(true)
    const h = setTimeout(() => {
      searchPicturesForProduct(term, 48)
        .then(r => { if (active) setResults(r) })
        .catch(() => { if (active) setResults([]) })
        .finally(() => { if (active) setLoading(false) })
    }, 250)
    return () => { active = false; clearTimeout(h) }
  }, [term])

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">Choose Inventory Picture</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3">
            <Search size={15} className="text-gray-400" />
            <input autoFocus value={term} onChange={e => setTerm(e.target.value)}
              placeholder="Search by name, plant type, or category…"
              className="flex-1 py-2 text-sm outline-none" />
          </div>
        </div>
        <div className="p-4 overflow-y-auto">
          {loading ? (
            <p className="text-sm text-gray-400 text-center py-8">Searching…</p>
          ) : results.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No pictures found</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {results.map(p => (
                <button key={p.id} onClick={() => onPick(p)}
                  className="text-left rounded-lg border border-gray-200 overflow-hidden hover:border-green-400 hover:shadow transition">
                  <div className="aspect-square bg-gray-100 flex items-center justify-center">
                    {p.image_url
                      ? <img src={p.image_url} alt={p.name ?? ''} className="object-cover w-full h-full" />
                      : <ImageIcon size={24} className="text-gray-300" />}
                  </div>
                  <div className="p-2">
                    <p className="text-xs font-medium text-gray-800 line-clamp-1">{p.name || '—'}</p>
                    <p className="text-xs text-gray-400 line-clamp-1">{[p.inventory_type, p.plant_type].filter(Boolean).join(' · ')}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
